import React, { useState, useMemo, useCallback } from 'react';
import { Typography, colors, Button } from '@sweep-io/sweep-design';
import { Stack } from '@mui/material';
import { InfoDialog } from '../../common/dialogs/InfoDialog';
import { DocumentationContextProvider } from '../../documentation/context/DocumentationContext';
import { DocumentationFiltersProvider } from '../../documentation/selected-object/filters/DocumentationFiltersContext';
import { AgentSelectionType } from '../../../types/enums/AgentType';
import { ObjectTabsForAgent } from './ObjectTabsForAgent';
import { MetadataTableForAgent } from './MetadataTableForAgent';
import { getObjectNameColor } from '../../documentation/helpers';
import pluralize from 'pluralize';
import { AiAgent } from '../aiAgentsConsts';
import { SelectionItem } from './AgentContextSelectionModal';
import useAiAgents from '../useAiAgents';
import { getAgentType, getObjectType, getMetadataType } from './utils';

interface AgentConfigurationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  agent: AiAgent;
}

export const AgentConfigurationsDialog: React.FC<AgentConfigurationsDialogProps> = ({
  isOpen,
  onClose,
  agent,
}) => {
  const { updateAgentConfigurations, refreshAgentIssues } = useAiAgents();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract existing configurations from agent
  const existingSelections = useMemo((): SelectionItem[] => {
    const contextComponents = agent.contextComponents;
    if (!contextComponents || !('includeConfigurations' in contextComponents)) {
      return [];
    }
    return contextComponents.includeConfigurations || [];
  }, [agent]);

  const agentSelectionType = getAgentType(agent);
  const objectType = getObjectType(agent);
  const metadataType = getMetadataType(agent);

  const [selections, setSelections] = useState<SelectionItem[]>(existingSelections);
  const totalSelected = selections.length;

  const modalTitle = useMemo(() => {
    const isMetadataType = agentSelectionType === AgentSelectionType.METADATA_TYPES;
    const displayText = isMetadataType ? metadataType : objectType;
    const backgroundColor = isMetadataType
      ? colors.grey[200]
      : getObjectNameColor(objectType || '');

    return (
      <Typography variant="h3">
        Monitoring Configurations{' '}
        <span
          style={{
            backgroundColor,
            padding: '4px 8px',
            borderRadius: '2px',
          }}
        >
          {displayText}
        </span>
      </Typography>
    );
  }, [agentSelectionType, objectType, metadataType]);

  const handleSelectionChange = useCallback(
    (newSelections: SelectionItem[]) => {
      if (isSubmitting) return;
      setSelections(newSelections);
    },
    [isSubmitting],
  );

  const handleApplyChanges = useCallback(async () => {
    setIsSubmitting(true);
    await updateAgentConfigurations(agent.id, selections);
    // Refresh issues table to reflect new configuration
    await refreshAgentIssues(agent.id);
    onClose();
    setIsSubmitting(false);
  }, [agent.id, selections, updateAgentConfigurations, refreshAgentIssues, onClose]);

  const dialogActions = (
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
      <Typography variant="body" color={colors.grey[700]}>
        {pluralize('item', totalSelected, true) + ' selected'}
      </Typography>
      <Button
        onClick={handleApplyChanges}
        disabled={isSubmitting || totalSelected === 0}
        loading={isSubmitting}
      >
        Apply changes
      </Button>
    </Stack>
  );

  const isObjectType = agentSelectionType === AgentSelectionType.OBJECT_TYPES;

  return (
    <InfoDialog
      showCloseButton={!isSubmitting}
      open={isOpen}
      handleClose={onClose}
      titleJsx={modalTitle}
      dialogActions={dialogActions}
      sx={{
        '& .MuiDialog-paper': {
          width: '90vw',
          maxWidth: '1400px',
          height: '85vh',
          maxHeight: 'none',
        },
      }}
    >
      <DocumentationContextProvider crmOrgId={agent.crmOrgId}>
        <DocumentationFiltersProvider>
          {isObjectType ? (
            <ObjectTabsForAgent
              crmOrgId={agent.crmOrgId}
              objectType={objectType!}
              onSelectionChange={handleSelectionChange}
              initialSelections={existingSelections}
            />
          ) : (
            <MetadataTableForAgent
              crmOrgId={agent.crmOrgId}
              metadataType={metadataType!}
              onSelectionChange={handleSelectionChange}
              initialSelections={existingSelections}
            />
          )}
        </DocumentationFiltersProvider>
      </DocumentationContextProvider>
    </InfoDialog>
  );
};
